import apiClient from '@/api/apiClient';
import { handleError, getAuthData, prepareModel, validateId } from '../../utils/storeHelpers';

export default {
  namespaced: true,
  state: {
    schema: {},
    catDiagnosticICD10: [],
    catDiagnosticICD11: [],
    catDiagnosticDSM5: [],
    dataCrud: [],
    dataCrud2: [],
    isNewRecord: true,
    status: '',
  },

  getters: {
    schema: (state) => state.schema,
    catDiagnosticICD10: (state) => state.catDiagnosticICD10,
    catDiagnosticICD11: (state) => state.catDiagnosticICD11,
    catDiagnosticDSM5: (state) => state.catDiagnosticDSM5,
    dataCrud: (state) => state.dataCrud,
    dataCrud2: (state) => state.dataCrud2,
    isNewRecord: (state) => state.isNewRecord,
    status: (state) => state.status,
  },

  mutations: {
    SET_SCHEMA(state, payload) {
      state.schema = payload?.schema;
      state.catDiagnosticICD10 = Array.isArray(payload?.catDiagnosticICD10)
        ? payload.catDiagnosticICD10
        : [];
      state.catDiagnosticICD11 = Array.isArray(payload?.catDiagnosticICD11)
        ? payload.catDiagnosticICD11
        : [];
      state.catDiagnosticDSM5 = Array.isArray(payload?.catDiagnosticDSM5)
        ? payload.catDiagnosticDSM5
        : [];
    },
    SET_RECORDS(state, { recordsData }) {
      state.dataCrud = Array.isArray(recordsData?.dataCrud) ? recordsData.dataCrud : [];
      state.dataCrud2 = Array.isArray(recordsData?.dataCrud2) ? recordsData.dataCrud2 : [];
      state.isNewRecord = Boolean(recordsData?.isNewRecord);
    },
    SET_STATUS(state, status) {
      state.status = status;
    },
  },

  actions: {
    async fetchSchema({ commit }) {
      try {
        commit('SET_STATUS', 'loading');
        const responses = await Promise.all([
          apiClient.dtxDiagTreatmentSchema(),
          apiClient.catDiagnosticManual(),
          apiClient.catDiagnosticICD10(),
          apiClient.catDiagnosticICD11(),
          apiClient.catDiagnosticDSM5(),
          apiClient.catDiagnosticType(),
          apiClient.catTreatmentActivity(),
          apiClient.catTreatmentSubject(),
          apiClient.catTreatmentObjective(),
          apiClient.catTreatmentDerivation(),
          apiClient.catInstitutions(),
          apiClient.catHealthServices(),
        ]);
        const [
          schemaRes,
          catDiagManual,
          catDiagICD10,
          catDiagICD11,
          catDiagDSM5,
          catDiagnosticType,
          catTreatmentActivity,
          catTreatmentSubject,
          catTreatmentObjective,
          catTreatmentDerivation,
          catInstitutions,
          catHealthServices,
        ] = responses.map((res) => res?.data ?? {});

        let catDiagnosticManualPre = catDiagManual.data ?? [];
        const catDiagnosticManual = JSON.stringify(catDiagnosticManualPre);

        let catDiagManualMental = JSON.parse(catDiagnosticManual);
        catDiagManualMental[0].type = 'mental';
        catDiagManualMental[1].type = 'mental';
        catDiagManualMental[2].type = 'mental';

        const catDiagnosticICD10 = catDiagICD10.data ?? [];

        let chapterVal = '06';
        let catDiagnosticICD11 = catDiagICD11.data ?? [];
        const catDiagnosticICD11Mental = catDiagnosticICD11.filter((item) =>
          item.diagnostic_icd_11_chapter.includes(chapterVal)
        );

        const catDiagnosticDSM5 = catDiagDSM5.data ?? [];

        if (!schemaRes.formCrud) throw new Error('Estructura de schema inválida.');
        if (!schemaRes.formCrud2) throw new Error('Estructura de schema inválida.');

        const schema = structuredClone(schemaRes);
        schema.formCrud.diagnostic.items = catDiagnosticICD11Mental;
        schema.formCrud.diagnostic_manual.items = catDiagManualMental;
        schema.formCrud.diagnostic_type_diagnostic_type_id.items = catDiagnosticType.data ?? [];
        schema.formCrud2.treatment_activity_treatment_activity_id.items =
          catTreatmentActivity.data ?? [];
        schema.formCrud2.treatment_subject_treatment_subject_id.items =
          catTreatmentSubject.data ?? [];
        schema.formCrud2.treatment_objective_treatment_objective_id.items =
          catTreatmentObjective.data ?? [];
        schema.formCrud2.treatment_derivation_treatment_derivation_id.items =
          catTreatmentDerivation.data ?? [];
        schema.formCrud2.treatment_institution_code.items = catInstitutions.data ?? [];
        schema.formCrud2.treatment_health_services_code.items = catHealthServices.data ?? [];

        commit('SET_SCHEMA', {
          schema,
          catDiagnosticICD10,
          catDiagnosticICD11,
          catDiagnosticDSM5,
        });
      } catch (error) {
        handleError(error, commit);
      }
    },

    async fetchRecords({ commit, rootState }) {
      try {
        const { clinicalHistories_id, institutions_profes_id } = getAuthData(rootState);
        const res = await apiClient.modelDTxDiagTreatment(clinicalHistories_id, institutions_profes_id);
        const recordsData = res?.data?.data ?? [];

        commit('SET_RECORDS', { recordsData });
        commit('SET_STATUS', 'loaded');
      } catch (error) {
        handleError(error, commit);
      }
    },

    async createRecord({ commit, rootState }, { model }) {
      try {
        commit('SET_STATUS', 'create');
        const preparedModel = prepareModel(rootState, model);
        await apiClient.createDTxDiagTreatment(preparedModel);
        commit('SET_STATUS', 'success');
      } catch (error) {
        handleError(error, commit);
      }
    },

    async updateRecord({ commit, rootState }, { model }) {
      try {
        commit('SET_STATUS', 'update');
        const { clinicalHistories_id } = getAuthData(rootState);
        const preparedModel = prepareModel(rootState, model);
        await apiClient.updateDTxDiagTreatment(clinicalHistories_id, preparedModel);
        commit('SET_STATUS', 'success');
      } catch (error) {
        handleError(error, commit);
      }
    },

    async removeRecord({ commit, rootState }, { id, type }) {
      try {
        commit('SET_STATUS', 'remove');
        const { clinicalHistories_id, institutions_profes_id } = getAuthData(rootState);
        validateId(id);
        await apiClient.deleteDTxDiagTreatment(id, clinicalHistories_id, institutions_profes_id, type);
        commit('SET_STATUS', 'success');
      } catch (error) {
        handleError(error, commit);
      }
    },
  },
};
